import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Grid } from '@material-ui/core';
import { css } from '@emotion/react';
import * as Sentry from '@sentry/react';

import { UserContext } from '@/components/App';
import { ModalContext } from '@/components/ShipmentForm/modals/EditOrderModal';
import OrderDetailsPricing from '@/components/ShipmentForm/modals/EditOrderModal/OrderDetailsTab/OrderDetailsPricing';
import { Body1, H1, H2, H3 } from '@/components/Accounting/Invoice/blocks';
import { colors } from '@/styles';

import { UPDATE_ORDER } from './mutations';
import { computeAggregate, getActivePerspectives } from './ratesTab.utils';

export { getActivePerspectives };

const dollarFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});

const formatCurrency = (n) => (Number.isFinite(n) ? dollarFormatter.format(n) : '–');

// A suppressed carrier payable is not a missing number — say so rather than showing an em dash.
const formatCarrier = (n) => (n === null ? 'N/A' : formatCurrency(n));

const formatPct = (n) => (Number.isFinite(n) ? `${n.toFixed(2)}%` : '–');

/**
 * Admin-only Rates tab on OrderDetailsPage. Shows aggregate Shipper/Carrier/Split-% at the
 * top, then one OrderDetailsPricing block per existing leg (minus internal). OrderDetailsPricing
 * is reused as-is via a ModalContext.Provider shim that mirrors the shape it expects from
 * EditOrderModal: { state.order, callbacks.modifyOrder }. Rate edits persist via UPDATE_ORDER.
 */
export const RatesTab = ({ order, refetchOrder }) => {
    const { user } = useContext(UserContext);
    const [updateOrder] = useMutation(UPDATE_ORDER, {
        onError: (err) => {
            console.error(err);
            Sentry.captureException(err);
        },
    });

    const isAdmin = useMemo(() => !!(user?.roles?.['ADMIN'] || user?.roles?.['ONWARD_ADMIN']), [user]);

    const activePerspectives = useMemo(() => getActivePerspectives(order), [order]);
    const aggregate = useMemo(() => computeAggregate(order), [order]);

    // Local optimistic clone so OrderDetailsPricing's setUpdates can read changes back.
    // Kept in sync with the latest server-fetched order via a key reset on refetch.
    const [clone, setClone] = useState(order);
    React.useEffect(() => {
        setClone(order);
    }, [order]);

    const modifyOrder = useCallback(
        async (changes) => {
            // Optimistic local merge so OrderDetailsPricing sees the new values immediately.
            setClone((prev) => ({ ...prev, ...changes }));
            // Persist. Strip any nested order_revenue_adjustments shapes that aren't valid
            // orders_set_input fields — for now pass changes through as-is and let Hasura
            // reject any unknown columns.
            try {
                await updateOrder({
                    variables: {
                        order_id: order.order_id,
                        _set: changes,
                    },
                });
                if (refetchOrder) refetchOrder();
            } catch (e) {
                Sentry.captureException(e);
                // Roll back optimistic update on failure.
                setClone(order);
            }
        },
        [order, updateOrder, refetchOrder]
    );

    const shimContextValue = useMemo(
        () => ({
            state: {
                order: clone,
            },
            callbacks: {
                modifyOrder,
            },
        }),
        [clone, modifyOrder]
    );

    if (!isAdmin) {
        // Belt-and-suspenders: tab visibility is gated upstream too.
        return null;
    }

    if (activePerspectives.length === 0) {
        return (
            <Grid
                container
                css={css`
                    padding: 2rem;
                `}
            >
                <Body1>No rates available yet — no carrier has been assigned to any leg.</Body1>
            </Grid>
        );
    }

    return (
        <ModalContext.Provider value={shimContextValue}>
            <Grid
                container
                direction="column"
                css={css`
                    padding: 2rem;
                    gap: 1.5rem;
                `}
            >
                <Grid
                    container
                    direction="row"
                    css={css`
                        background-color: ${colors.greys?.[20] || '#f5f5f5'};
                        padding: 16px 24px;
                        border-radius: 4px;
                        align-items: center;
                        justify-content: space-between;
                    `}
                >
                    <Grid item>
                        <Body1>Shipper Rate</Body1>
                        <H2>{formatCurrency(aggregate.shipper)}</H2>
                    </Grid>
                    <Grid item>
                        <Body1>Carrier Rate</Body1>
                        <H2>{formatCarrier(aggregate.carrier)}</H2>
                    </Grid>
                    <Grid item>
                        <Body1>Contractor Rate</Body1>
                        <H2>{formatCurrency(aggregate.contractor)}</H2>
                    </Grid>
                    <Grid item>
                        <Body1>Split %</Body1>
                        <H2>{formatPct(aggregate.splitPct)}</H2>
                    </Grid>
                </Grid>

                {activePerspectives.map((perspective) => (
                    <OrderDetailsPricing key={perspective} perspective={perspective} />
                ))}
            </Grid>
        </ModalContext.Provider>
    );
};

export default RatesTab;
